/**
 * BoostRemapper
 *
 * Bridges the raw `boosts` Remote Config JSON → BoostDto → BoostEntity.
 * `fromJson` / `fromJsonList` are the only raw-JSON entry points; the payload is
 * an operator-edited console string (Flutter reads the same key,
 * `lib/services/remote_config_service.dart:84-108`), so every field applies the
 * Flutter constructor default defensively rather than trusting the wire shape.
 * That per-field leniency stops at `options`: a tier that ends up with none is
 * dropped in `fromDtoList` (see its doc), because every consumer indexes
 * `options[0]` unguarded.
 *
 * Seeds local-only `isSelected` from `preselect ?? false`. If no boost has
 * `preselect`, the first one is selected; same for options. Matches Flutter
 * `firstWhere(..., orElse: () => list[0])` behavior.
 */
import type { BoostDto } from '@/data/dtos/boost/boostDto';
import type { BoostOptionDto } from '@/data/dtos/boost/boostOptionDto';
import type { JsonObject } from '@/data/types/json';
import type { BoostEntity } from '@/domain/entities/boost/boostEntity';
import type { BoostOptionEntity } from '@/domain/entities/boost/boostOptionEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class BoostRemapper {
  /**
   * Flutter: `BoostOption.fromJson` (`lib/data/models/boost_option.dart:5-27`).
   * No `@JsonKey` renames, so each JSON key equals the property name;
   * `preselect` is constructor-defaulted to `false` (`boost_option.dart:21`) and
   * `isSelected` is local-only, hence absent from the wire shape.
   */
  private static fromOptionJson(data: JsonObject): BoostOptionDto {
    return {
      id: (data['id'] as string | undefined) ?? '',
      days: (data['days'] as number | undefined) ?? 0,
      price: (data['price'] as number | undefined) ?? 0,
      durationDisplay: (data['durationDisplay'] as string | undefined) ?? '',
      preselect: (data['preselect'] as boolean | undefined) ?? false,
    };
  }

  /**
   * Flutter: `Boost.fromJson` (`lib/data/models/boost.dart:6-30`).
   * `highlight` (`boost.dart:25`) and `preselect` (`boost.dart:26`) default to
   * `false`. A missing or non-array `benefits` / `options` degrades to an empty
   * list rather than throwing.
   */
  static fromJson(data: JsonObject): BoostDto {
    const rawBenefits = data['benefits'];
    const rawOptions = data['options'];
    return {
      id: (data['id'] as string | undefined) ?? '',
      name: (data['name'] as string | undefined) ?? '',
      description: (data['description'] as string | undefined) ?? '',
      benefits: Array.isArray(rawBenefits) ? (rawBenefits as string[]) : [],
      highlight: (data['highlight'] as boolean | undefined) ?? false,
      preselect: (data['preselect'] as boolean | undefined) ?? false,
      options: Array.isArray(rawOptions)
        ? (rawOptions as JsonObject[]).map(BoostRemapper.fromOptionJson)
        : [],
    };
  }

  static fromJsonList(dataList: JsonObject[]): BoostDto[] {
    return dataList.map(BoostRemapper.fromJson);
  }

  static fromOptionDto(dto: BoostOptionDto): BoostOptionEntity {
    return {
      id: dto.id,
      days: dto.days,
      price: dto.price,
      durationDisplay: dto.durationDisplay,
      preselect: dto.preselect,
      isSelected: dto.preselect ?? false,
    };
  }

  static fromDto(dto: BoostDto): BoostEntity {
    const options = dto.options.map((o) => BoostRemapper.fromOptionDto(o));
    BoostRemapper.ensureOneSelected(options);
    return {
      id: dto.id,
      name: dto.name,
      description: dto.description,
      benefits: [...dto.benefits],
      highlight: dto.highlight,
      preselect: dto.preselect,
      options,
      isSelected: dto.preselect ?? false,
    };
  }

  /**
   * A console-template tier with no `options` is unusable: `BoostCard.tsx:39`
   * and `useContentMonetization.ts:30` both fall back to `options[0]` and read
   * `.price` off it, and `cycleBoostOption` divides by `options.length` — an
   * optionless tier would throw during the monetization step's render.
   *
   * Flutter drops the WHOLE catalog on such a payload: `Boost.fromJson` casts
   * `json['options'] as List<dynamic>` (`lib/data/models/boost.g.dart:14-16`)
   * with no fallback, so a missing/non-list `options` throws and the outer
   * try/catch at `lib/services/remote_config_service.dart:104-107` logs and
   * returns `[]`. Dropping only the malformed tier is the closest safe web
   * equivalent while keeping the well-formed siblings usable.
   *
   * The filter runs before `ensureOneSelected` so selection seeding is computed
   * over the surviving tiers only (dropping a preselected-but-optionless tier
   * must still leave exactly one boost selected).
   */
  static fromDtoList(dtos: BoostDto[]): BoostEntity[] {
    const usable = dtos.filter((dto) => dto.options.length > 0);

    if (usable.length !== dtos.length) {
      appLogger.error(
        'Dropped boost tier(s) with no options from the remote config catalog',
        'BoostRemapper.fromDtoList',
        {
          context: {
            droppedIds: dtos
              .filter((dto) => dto.options.length === 0)
              .map((dto) => dto.id)
              .join(','),
            droppedCount: dtos.length - usable.length,
          },
        },
      );
    }

    const boosts = usable.map((d) => BoostRemapper.fromDto(d));
    BoostRemapper.ensureOneSelected(boosts);
    return boosts;
  }

  private static ensureOneSelected<T extends { isSelected: boolean }>(items: T[]): void {
    if (items.length === 0) return;
    if (items.some((i) => i.isSelected)) return;
    items[0].isSelected = true;
  }
}
